"use client";

import { useRef, useEffect, useState, useCallback } from "react";
import { t } from "@/utils";
import {
	HmcBannerMessage,
	HmcButton,
} from "@adi-ctx/harmonic-core-components-react";
import type { TPropsWithOptionalChildren } from "@/types";

// https://harmonic.design-system.analogtools.io/2.4.0/index.html?path=/info/components-banner-message--statuses
type NotificationProps = {
	[key: string]: any;
	readonly type: "error" | "info" | "success" | "warning";
	readonly text?: string;
	readonly actionOnClient?: (arg: any) => void; // client function for the "action"
	readonly actionButtonText?: string;
	readonly actionOnServer?: () => void; // server function for the "action"
} & TPropsWithOptionalChildren;

/**
 * Display a Harmonic Banner component for notifications
 * @param type
 * @param text
 * @param actionOnClient
 * @param actionButtonText
 * @param actionOnServer
 * @param props
 * @constructor
 */
function Notification({
	type,
	text,
	actionOnClient,
	actionButtonText,
	actionOnServer,
	...props
}: NotificationProps) {
	const [isRetryInProgress, setIsRetryInProgress] = useState(false);
	const ref = useRef<any>(null);

	const retry = useCallback(() => {
		// Just add a visual hint that there is an ongoing process
		setIsRetryInProgress(true);
		setTimeout(() => {
			setIsRetryInProgress(false);
		}, 2000);

		if (typeof actionOnServer === "function") {
			actionOnServer();
		}
	}, [actionOnServer]);

	// Adding some CSS fixes to the Notification bar
	useEffect(() => {
		if (!ref.current?.shadowRoot?.adoptedStyleSheets) return;

		const newStyleSheet = new CSSStyleSheet();
		newStyleSheet.insertRule(".banner { display: flex; align-items: center }");
		newStyleSheet.insertRule(".banner hmc-icon { padding-top: 0 }");
		newStyleSheet.insertRule(
			".content-container { flex-grow: 1; align-items: center }"
		);
		newStyleSheet.insertRule(".main-content { display: flex; flex-grow: 1 }");

		const elemStyleSheets = ref.current.shadowRoot?.adoptedStyleSheets || [];
		ref.current.shadowRoot.adoptedStyleSheets = [
			...elemStyleSheets,
			newStyleSheet,
		];
	}, []);

	return (
		<HmcBannerMessage
			ref={ref}
			withIcon
			actionText={actionOnClient ? actionButtonText ?? t("error.retry") : ""}
			density="low"
			isClosable={false}
			status={type}
			onActionClicked={actionOnClient}
			{...props}
		>
			<div className="flex grow items-center">
				<span className="grow">{text}</span>

				{actionOnServer && (
					<form action={retry}>
						<HmcButton
							isDisabled={isRetryInProgress}
							autofocus={false}
							kind="secondary"
							type="submit"
							size="sm"
						>
							{t("error.retry")}
						</HmcButton>
					</form>
				)}
			</div>
		</HmcBannerMessage>
	);
}

export default Notification;
